import React, { memo, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useHorizontalSwipe } from '../../hooks/useSwipe';
import { getTouchTargetClasses } from '../../utils/responsive';
import { useResponsivePerformance } from '../../hooks/useResponsivePerformance';
import { keyboardNavigation, announceToScreenReader } from '../../utils/accessibility';
import { useAuth } from '../../context/AuthContext';
import { Lock } from 'lucide-react';
import useUIStore from '../../store/uiStore';
import { usePermissions } from '../../hooks/usePermissions';

const Sidebar = memo(() => {
  const { currentUser: user } = useAuth();
  const navigate = useNavigate();
  
  // Zustand store - solo usamos openUpgradeModal, el modal se renderiza en Layout.jsx
  const { 
    isSidebarOpen, 
    closeSidebar, 
    openUpgradeModal 
  } = useUIStore();
  
  const { isMobile } = useResponsivePerformance();
  
  // === PERMISOS UNIFICADOS — usan usePermissions hook (fuente única de verdad) ===
  const {
    canAccessInventory,
    canAccessFinance,
    canNavigateApp,
    planName,
  } = usePermissions();

  // Build menu items based on permissions
  const menuItems = [
    { path: '/app/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/app/pacientes', label: 'Pacientes', icon: '👥' },
    { path: '/app/citas', label: 'Citas', icon: '📅' },
    { path: '/app/fichas-clinicas', label: 'Fichas Clínicas', icon: '📋' },
    // Mostrar Equipo a administradores o superusuarios
    ...((user?.rol === 'admin' || user?.is_superuser) ? [{ path: '/app/equipo', label: 'Equipo', icon: '🧠' }] : []),
  ];

  const handleNavClick = (label) => {
    announceToScreenReader(`Navegando a ${label}`);
    if (isMobile) {
      closeSidebar();
    }
  };

  // Manejar tecla Escape para cerrar sidebar en móvil
  useEffect(() => {
    if (!isSidebarOpen || !isMobile) return;

    const cleanup = keyboardNavigation.handleEscape(() => {
      closeSidebar();
    });

    return cleanup;
  }, [isSidebarOpen, isMobile, closeSidebar]);

  // Configurar swipe para cerrar sidebar en móvil
  const swipeRef = useHorizontalSwipe(
    () => {
      if (isSidebarOpen) {
        closeSidebar();
      }
    },
    null,
    {
      threshold: 50,
      preventDefaultTouchmoveEvent: false,
    }
  );

  // Handler para items bloqueados
  const handleLockedClick = (e, featureName) => {
    e.preventDefault();
    openUpgradeModal(featureName);
  };

  return (
    <nav 
      ref={swipeRef}
      style={{ height: '100dvh' }}
      className={`
        bg-primary-dark text-white w-64 p-4 
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:inset-0
      `}
      aria-label="Navegación principal"
    >
      {/* Close button for mobile */}
      <div className="flex justify-end lg:hidden mb-4">
        <button
          onClick={closeSidebar}
          className={`p-2 rounded-md text-primary-light hover:text-white hover:bg-primary focus:ring-white touch-feedback ${getTouchTargetClasses()}`}
          aria-label="Cerrar menú"
        >
          <span className="sr-only">Cerrar menú</span>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Swipe indicator for mobile */}
      {isMobile && (
        <div className="mb-2 text-center">
          <div className="inline-flex items-center text-xs text-gray-400 opacity-60">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Desliza para cerrar
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col items-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          {user?.cuenta?.logo ? (
            <img 
              src={user.cuenta.logo} 
              alt={`${user.cuenta.nombre || 'Clínica'} Logo`} 
              className="h-12 w-12 object-contain rounded-lg bg-white/10 p-1 drop-shadow-lg"
            />
          ) : (
            <img 
              src="/logo-symbol.png" 
              alt="EstetikFlow Logo" 
              className="h-12 w-auto drop-shadow-lg"
            />
          )}
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
            {user?.cuenta?.nombre || 'EstetikFlow'}
          </h1>
        </div>
        <p className="text-primary-light text-xs sm:text-sm text-center">Panel de Administración</p>
      </div>

      <div className="space-y-2">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => handleNavClick(item.label)}
            className={({ isActive }) =>
              `flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${isActive
                ? 'bg-secondary text-primary-dark font-semibold'
                : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
              }`
            }
            aria-label={`Ir a ${item.label}`}
            role="menuitem"
          >
            <span className="text-lg" aria-hidden="true">{item.icon}</span>
            <span className="text-sm sm:text-base">{item.label}</span>
          </NavLink>
        ))}

        {/* Inventario - Solo Imperio Profesional y Gabinete Expansión */}
        <NavLink
          to="/app/inventario"
          onClick={(e) => {
            if (!canAccessInventory) {
              handleLockedClick(e, 'Inventario Avanzado');
            } else {
              handleNavClick('Inventario');
            }
          }}
          className={({ isActive }) =>
            `flex items-center justify-between px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${
              !canAccessInventory 
                ? 'text-white/50 hover:bg-primary/50 hover:text-white/70'
                : isActive
                  ? 'bg-secondary text-primary-dark font-semibold'
                  : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
            }`
          }
          aria-label={canAccessInventory ? 'Ir a Inventario' : 'Inventario - Función Premium'}
          role="menuitem"
        >
          <div className="flex items-center space-x-2">
            <span className={`text-lg ${!canAccessInventory ? 'opacity-50' : ''}`} aria-hidden="true">📦</span>
            <span className="text-sm sm:text-base">Inventario</span>
          </div>
          {!canAccessInventory && <Lock className="w-4 h-4 text-amber-400" />}
        </NavLink>

        {/* Rentabilidad - Cualquier plan pagado */}
        <NavLink
          to="/app/rentabilidad"
          onClick={(e) => {
            if (!canAccessFinance) {
              handleLockedClick(e, 'Rentabilidad Financiera');
            } else {
              handleNavClick('Rentabilidad');
            }
          }}
          className={({ isActive }) =>
            `flex items-center justify-between px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${
              !canAccessFinance 
                ? 'text-white/50 hover:bg-primary/50 hover:text-white/70'
                : isActive
                  ? 'bg-secondary text-primary-dark font-semibold'
                  : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
            }`
          }
          aria-label={canAccessFinance ? 'Ir a Rentabilidad' : 'Rentabilidad - Función Premium'}
          role="menuitem"
        >
          <div className="flex items-center space-x-2">
            <span className={`text-lg ${!canAccessFinance ? 'opacity-50' : ''}`} aria-hidden="true">💰</span>
            <span className="text-sm sm:text-base">Rentabilidad</span>
          </div>
          {!canAccessFinance && <Lock className="w-4 h-4 text-amber-400" />}
        </NavLink>

        <NavLink
          to="/app/suscripcion"
          onClick={() => handleNavClick('Suscripción')}
          className={({ isActive }) =>
            `flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${isActive
              ? 'bg-secondary text-primary-dark font-semibold'
              : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
            }`
          }
          aria-label="Ir a Mi Suscripción"
          role="menuitem"
        >
          <span className="text-lg" aria-hidden="true">💎</span>
          <span className="text-sm sm:text-base">Mi Suscripción</span>
        </NavLink>

        {/* Configuración - Para todos los usuarios autenticados */}
        <NavLink
          to="/app/configuracion"
          onClick={() => handleNavClick('Configuración')}
          className={({ isActive }) =>
            `flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${isActive
              ? 'bg-secondary text-primary-dark font-semibold'
              : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
            }`
          }
          aria-label="Ir a Configuración de Clínica"
          role="menuitem"
        >
          <span className="text-lg" aria-hidden="true">⚙️</span>
          <span className="text-sm sm:text-base">Configuración</span>
        </NavLink>

        {/* Sección de Administración SaaS */}
        {user?.is_superuser && (
          <>
            <div className="pt-4 pb-2 px-4">
              <p className="text-xs font-semibold text-primary-light uppercase tracking-wider">
                Administración SaaS
              </p>
            </div>
            <NavLink
              to="/app/admin/clinicas"
              onClick={() => handleNavClick('Clínicas')}
              className={({ isActive }) =>
                `flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${isActive
                  ? 'bg-secondary text-primary-dark font-semibold'
                  : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
                }`
              }
              aria-label="Ir a Gestión de Clínicas"
              role="menuitem"
            >
              <span className="text-lg" aria-hidden="true">🏢</span>
              <span className="text-sm sm:text-base">Clínicas</span>
            </NavLink>
            <NavLink
              to="/app/respaldo"
              onClick={() => handleNavClick('Respaldo')}
              className={({ isActive }) =>
                `flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors touch-feedback ${getTouchTargetClasses()} ${isActive
                  ? 'bg-secondary text-primary-dark font-semibold'
                  : 'text-white/80 hover:bg-primary hover:text-white active:bg-primary-dark focus:bg-primary'
                }`
              }
              aria-label="Ir a Respaldo de Base de Datos"
              role="menuitem"
            >
              <span className="text-lg" aria-hidden="true">💾</span>
              <span className="text-sm sm:text-base">Respaldo</span>
            </NavLink>
          </>
        )}
      </div>

      {/* NO HAY MODAL AQUÍ - El modal global está en Layout.jsx */}
    </nav>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;